import React, { ReactElement, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { CircularProgress, Grid } from '@material-ui/core'
import clsx from 'clsx'

import Headline from '@objects/headline'
import Button from '@objects/button'

import { useAutobahnplakateTexts } from '../store'
import { Colors } from '@config/theme/palette'
import FontSize from '@config/theme/definitions/fontSize'

import { sendVote, getResults } from '@services/api/autobahnVotingPhase1'

import { SWIPER_SPEED_MS } from '../index'
import { setVotedInStorage } from '../helper/storage'

const useStyles = makeStyles((theme) => ({
  root: {},
  headline: {
    fontWeight: 900,
    fontSize: '1.5rem',
    textAlign: 'center',
    margin: 0,
    '& .highlight': {
      color: Colors.green[500],
    },

    [theme.breakpoints.up('md')]: {
      fontSize: '2rem',
      padding: theme.spacing(0, 20),
    },

    [theme.breakpoints.up('lg')]: {
      fontSize: '2rem',
      padding: theme.spacing(0, 45),
    },
  },
  itemsContainer: {
    marginTop: theme.spacing(6),
    marginBottom: theme.spacing(12),
    justifyContent: 'center',
    [theme.breakpoints.up('md')]: {
      marginTop: theme.spacing(8),
      marginBottom: theme.spacing(12),
    },
  },
  hideLabel: {
    opacity: 0,
  },
  spinner: {
    color: theme.palette.button.mainText,
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    opacity: 0,
  },
  showSpinner: {
    opacity: 1,
  },
  buttonWrapper: {
    textAlign: 'center',
  },
}))

const useItemStyles = makeStyles((theme) => ({
  item: {
    padding: theme.spacing(2),
    position: 'relative' as const,
  },

  radio: {
    position: 'absolute' as const,
    opacity: 0,
    cursor: 'pointer',
    height: 0,
    width: 0,

    '&:checked + label': {
      backgroundColor: '#F9FFB9',
    },

    '&:focus-visible + label': {
      outline: `2px solid ${theme.palette.button.main}`,
      outlineOffset: '2px',
    },
  },

  label: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(3),
    padding: theme.spacing(2, 3),
    border: `1px solid ${Colors.green[500]}`,
    borderRadius: theme.spacing(2),
    cursor: 'pointer',
    backgroundColor: theme.palette.background.default,
    transition:
      'background-color 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease',
    boxShadow: '0px 4px 6px 0px rgba(0, 0, 0, 0.15)',
    fontFamily: theme.typography.fontFamily2,
    width: '100%',
    lineHeight: 1.3,

    '&:hover': {
      transform: 'translateY(2px)',
      boxShadow: '0px 3px 6px 0px rgba(0, 0, 0, 0.05)',
    },
  },

  labelSmall: {
    [theme.breakpoints.up('lg')]: {
      width: '60%',
    },
  },

  labelLarge: {
    [theme.breakpoints.up('lg')]: {
      width: '85%',
    },
  },

  rightAlign: {
    [theme.breakpoints.up('lg')]: {
      marginLeft: 'auto',
    },
  },

  labelId: {
    backgroundColor: Colors.green[500],
    color: theme.palette.button.mainText,
    borderRadius: theme.spacing(1),
    width: theme.spacing(10),
    height: theme.spacing(10),
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 900,
    fontSize: FontSize['2lg'],

    [theme.breakpoints.up('lg')]: {
      fontSize: FontSize['xl'],
      width: theme.spacing(14),
      height: theme.spacing(14),
    },
  },

  labelText: {
    flex: 1,
    [theme.breakpoints.up('lg')]: {
      textAlign: 'center',
    },
  },
}))

export type VotingBoxProps = {
  id: string
  label: string
  onChange?: (label: string) => void
  token: string
}

function VotingBox({
  token,
  id,
  label,
  onChange,
}: VotingBoxProps): ReactElement {
  const classes = useItemStyles()
  const store = useAutobahnplakateTexts()
  const votingLabels = [
    store.votingItemLabel1,
    store.votingItemLabel2,
    store.votingItemLabel3,
    store.votingItemLabel4,
    store.votingItemLabel5,
  ]
  const votingLabelsCount = votingLabels.filter((label) => label).length
  const isLastLabel = Number(id) === votingLabelsCount
  const isLastAndOdd = isLastLabel && Number(id) % 2 !== 0
  return (
    <Grid
      item
      xs={12}
      md={6}
      className={classes.item}
      justifyContent={'center'}
      container={isLastAndOdd}
    >
      <input
        type="radio"
        name="voting"
        id={`voting-item-${token}`}
        onChange={() => onChange?.(token)}
        className={classes.radio}
      />
      <label
        htmlFor={`voting-item-${token}`}
        className={clsx(classes.label, {
          [classes.labelSmall]: id === '01' || id === '02' || isLastAndOdd,
          [classes.labelLarge]:
            ((id === '03' || id === '05') && Number(id) < votingLabelsCount) ||
            id === '04',
          [classes.rightAlign]:
            (id === '01' || id === '03') && Number(id) < votingLabelsCount,
        })}
      >
        <span className={classes.labelId}>{id}</span>
        <span className={classes.labelText}>
          {label.charAt(0).toUpperCase() + label.slice(1)}
        </span>
      </label>
    </Grid>
  )
}

export default function Voting(): ReactElement {
  const classes = useStyles()
  const store = useAutobahnplakateTexts()
  const [loading, setLoading] = useState(false)
  const [sent, setSent] = useState(false)

  const done = () => {
    store.next()
    setTimeout(() => {
      setLoading(false)
      setSent(true)

      setVotedInStorage()
    }, SWIPER_SPEED_MS)
  }

  const vote = () => {
    if (sent) return // prevent multiple sends

    setLoading(true)

    if (store.selectedVote) {
      sendVote(store.selectedVote)
        .then((res) => {
          if (res && res.status === 201) {
            getResults()
              .then((res) => {
                if (res && res.status === 200) {
                  store.setResults(res.data)
                }
                done()
              })
              .catch(done)
          } else {
            setLoading(false)
          }
        })
        .catch(() => {
          setLoading(false)
        })
    }
  }

  return (
    <div className={classes.root}>
      <Headline level={2} className={classes.headline}>
        {!!store.votingHeadlineHighlightedWord ? (
          <span
            dangerouslySetInnerHTML={{
              __html: store.votingHeadline.replace(
                store.votingHeadlineHighlightedWord,
                `<span class="highlight">${store.votingHeadlineHighlightedWord}</span>`
              ),
            }}
          />
        ) : (
          store.votingHeadline
        )}
      </Headline>

      <Grid container wrap="wrap" className={classes.itemsContainer}>
        <VotingBox
          id="01"
          token={store.options[0]?.token}
          label={store.votingItemLabel1}
          onChange={store.setSelectedVote}
        />
        <VotingBox
          id="02"
          token={store.options[1]?.token}
          label={store.votingItemLabel2}
          onChange={store.setSelectedVote}
        />
        <VotingBox
          id="03"
          token={store.options[2]?.token}
          label={store.votingItemLabel3}
          onChange={store.setSelectedVote}
        />
        {!!store.votingItemLabel4 && !!store.options[3]?.token && (
          <VotingBox
            id="04"
            token={store.options[3]?.token}
            label={store.votingItemLabel4}
            onChange={store.setSelectedVote}
          />
        )}
        {!!store.votingItemLabel5 && !!store.options[4]?.token && (
          <VotingBox
            id="05"
            token={store.options[4]?.token}
            label={store.votingItemLabel5}
            onChange={store.setSelectedVote}
          />
        )}
      </Grid>

      <div className={classes.buttonWrapper}>
        <Button onClick={vote} disabled={loading || !store.selectedVote}>
          <div className={clsx({ [classes.hideLabel]: loading })}>
            {store.votingButtonLabel}
          </div>

          <div
            className={clsx(classes.spinner, {
              [classes.showSpinner]: loading,
            })}
          >
            <CircularProgress size={28} color="inherit" />
          </div>
        </Button>
      </div>
    </div>
  )
}
